import React from "react";
import classnames from "classnames";
import {
  EmployerAccountPending,
  EmployerApplicationApproved,
  EmployerApplicationDenied,
  EmployerApplicationAdditionalDocumentationSubmitted,
  EmployerApplicationRequiresAdditionalDocumentation,
  EmployerApplicationSubmittedKYB,
  EmployerRegistrationComplete,
  EmployerSelfServeRegistrationComplete,
  EmployerSelfServeControllerComplete,
  EmployerSelfServeTermsComplete,
  EmployerCreated,
  EmployerTermsComplete,
  EmployerBillingVerificationPending,
  EmployerPendingKYBSubmission,
  EmployerClosed,
  EmployerClosePending,
  EmployerAdminActive,
  EmployerAdminLoginCreated,
  EmployerRequiredUpdatesPending,
  EmployerSelfServeControllerRegistrationPending,
  EmployerBillingDetailsPending,
  EmployerSelfServeBillingDetailsPending,
  EmployerBillingDetailsComplete,
  EmployerSelfServeBillingDetailsComplete,
  IndividualSubmitSsnPending,
  IndividualEmailVerificationPending,
  IndividualLoginCreated,
  IndividualRolloverInfoPending,
  IndividualEmailVerificationComplete,
  IndividualApplicationInfoComplete,
  IndividualInvestmentInfoComplete,
  IndividualInvestmentInfo,
  IndividualDisclosures,
  IndividualDisclosuresComplete,
  IndividualTermsAccepted,
  IndividualKYC1Green,
  IndividualKYC2Yellow,
  IndividualApplicationRestartedKYC2,
  IndividualKYC1Red,
  IndividualKYC2Red,
  IndividualApplicationSubmittedKYC1,
  IndividualApplicationSubmittedKYC2,
  IndividualApplicationApproved,
  IndividualApplicationRequiresAdditionalDocumentation,
  IndividualApplicationAdditionalDocumentationSubmitted,
  IndividualApplicationDenied,
  IndividualReopened,
  IndividualClosed,
  IndividualLocked,
  IndividualDormant,
  IndividualActive,
  IndividualPortfolioPending,
  IndividualClosePending,
  IndividualContributionPending,
  IndividualApplicationAppealSubmitted,
  IndividualApplicationAppealDocumentationAdminReviewPending,
  IndividualApplicationRequiresAdditionalDocumentationForAppeal,
  IndividualInvestmentProfileUpdate,
  IndividualPortfolioUpdate,
  IndividualTermsPending,
  IndividualPayrollContributionPending,
  IndividualAccountTypeSelectionPending,
  IndividualSelectedAccountType,
  IndividualRequiredUpdatesPending,
  IndividualApplicationAdditionalDocumentationSubmittedKYC1,
  IndividualApplicationRequiresAdditionalDocumentationKYC1,
  IndividualApplicationRequiresAdditionalDocumentationForAppealKYC1,
  IndividualApplicationAppealSubmittedKYC1,
  IndividualApplicationAppealDocumentationAdminReviewPendingKYC1,
  IndividualApplicationAttestationSubmitted,
  IndividualCompletedForms,
  AccountActive,
  AccountClosePending,
  AccountClosed,
  AccountLocked,
  AccountApplicationSubmitted,
  AccountApplicationApproved,
  AccountApplicationDenied,
  AccountApplicationApexAutoAppeal,
  AccountApplicationAdditionalApexDocumentationSubmitted,
  AccountApplicationApexDocumentsRequired,
  BusinessMemberKYCRequested,
  BusinessMemberKYCFailed,
} from "../statics/states";
import "./KycStatusBadge.scss";

// Status mapping with colors and display text
const STATUS_CONFIG: Record<string, { color: string; text: string }> = {
  // Green - Active/Complete/Approved states
  [EmployerApplicationApproved]: {
    color: "green",
    text: "Application Approved",
  },
  [EmployerRegistrationComplete]: {
    color: "green",
    text: "Registration Complete",
  },
  [EmployerSelfServeRegistrationComplete]: {
    color: "green",
    text: "Self-Serve Registration Complete",
  },
  [EmployerSelfServeControllerComplete]: {
    color: "green",
    text: "Controller Complete",
  },
  [EmployerSelfServeTermsComplete]: { color: "green", text: "Terms Complete" },
  [EmployerTermsComplete]: { color: "green", text: "Terms Complete" },
  [EmployerBillingDetailsComplete]: {
    color: "green",
    text: "Billing Complete",
  },
  [EmployerSelfServeBillingDetailsComplete]: {
    color: "green",
    text: "Self-Serve Billing Complete",
  },
  [EmployerAdminActive]: { color: "green", text: "Admin Active" },

  [IndividualEmailVerificationComplete]: {
    color: "green",
    text: "Email Verified",
  },
  [IndividualApplicationInfoComplete]: {
    color: "green",
    text: "Application Info Complete",
  },
  [IndividualInvestmentInfoComplete]: {
    color: "green",
    text: "Investment Info Complete",
  },
  [IndividualDisclosuresComplete]: {
    color: "green",
    text: "Disclosures Complete",
  },
  [IndividualTermsAccepted]: { color: "green", text: "Terms Accepted" },
  [IndividualKYC1Green]: { color: "green", text: "KYC1 Approved" },
  [IndividualApplicationApproved]: {
    color: "green",
    text: "Application Approved",
  },
  [IndividualActive]: { color: "green", text: "Individual Active" },
  [IndividualSelectedAccountType]: {
    color: "green",
    text: "Account Type Selected",
  },
  [IndividualCompletedForms]: { color: "green", text: "Forms Complete" },

  [AccountActive]: { color: "green", text: "Account Active" },
  [AccountApplicationApproved]: {
    color: "green",
    text: "Application Approved",
  },

  // Orange/Yellow - Pending/In Progress states
  [EmployerAccountPending]: { color: "orange", text: "Account Pending" },
  [EmployerApplicationSubmittedKYB]: { color: "orange", text: "KYB Submitted" },
  [EmployerCreated]: { color: "orange", text: "Created" },
  [EmployerBillingVerificationPending]: {
    color: "orange",
    text: "Billing Verification Pending",
  },
  [EmployerPendingKYBSubmission]: {
    color: "orange",
    text: "KYB Submission Pending",
  },
  [EmployerClosePending]: { color: "orange", text: "Close Pending" },
  [EmployerRequiredUpdatesPending]: {
    color: "orange",
    text: "Updates Required",
  },
  [EmployerSelfServeControllerRegistrationPending]: {
    color: "orange",
    text: "Controller Registration Pending",
  },
  [EmployerBillingDetailsPending]: {
    color: "orange",
    text: "Billing Details Pending",
  },
  [EmployerSelfServeBillingDetailsPending]: {
    color: "orange",
    text: "Self-Serve Billing Pending",
  },

  [IndividualSubmitSsnPending]: {
    color: "orange",
    text: "SSN Submission Pending",
  },
  [IndividualEmailVerificationPending]: {
    color: "orange",
    text: "Email Verification Pending",
  },
  [IndividualRolloverInfoPending]: {
    color: "orange",
    text: "Rollover Info Pending",
  },
  [IndividualKYC2Yellow]: { color: "orange", text: "KYC2 Review" },
  [IndividualApplicationRestartedKYC2]: {
    color: "orange",
    text: "KYC2 Restarted",
  },
  [IndividualPortfolioPending]: { color: "orange", text: "Portfolio Pending" },
  [IndividualClosePending]: { color: "orange", text: "Close Pending" },
  [IndividualContributionPending]: {
    color: "orange",
    text: "Contribution Pending",
  },
  [IndividualInvestmentProfileUpdate]: {
    color: "orange",
    text: "Investment Profile Update",
  },
  [IndividualPortfolioUpdate]: { color: "orange", text: "Portfolio Update" },
  [IndividualTermsPending]: { color: "orange", text: "Terms Pending" },
  [IndividualPayrollContributionPending]: {
    color: "orange",
    text: "Payroll Contribution Pending",
  },
  [IndividualAccountTypeSelectionPending]: {
    color: "orange",
    text: "Account Type Selection Pending",
  },
  [IndividualRequiredUpdatesPending]: {
    color: "orange",
    text: "Updates Required",
  },

  [AccountClosePending]: { color: "orange", text: "Close Pending" },

  // Yellow - Additional Documentation Required
  [EmployerApplicationAdditionalDocumentationSubmitted]: {
    color: "yellow",
    text: "Additional Docs Submitted",
  },
  [EmployerApplicationRequiresAdditionalDocumentation]: {
    color: "yellow",
    text: "Additional Docs Required",
  },

  [IndividualApplicationRequiresAdditionalDocumentation]: {
    color: "yellow",
    text: "Additional Docs Required",
  },
  [IndividualApplicationAdditionalDocumentationSubmitted]: {
    color: "yellow",
    text: "Additional Docs Submitted",
  },
  [IndividualApplicationAppealSubmitted]: {
    color: "yellow",
    text: "Appeal Submitted",
  },
  [IndividualApplicationAppealDocumentationAdminReviewPending]: {
    color: "yellow",
    text: "Appeal Docs Required",
  },
  [IndividualApplicationRequiresAdditionalDocumentationForAppeal]: {
    color: "yellow",
    text: "Appeal Docs Required",
  },
  [IndividualApplicationAdditionalDocumentationSubmittedKYC1]: {
    color: "yellow",
    text: "KYC1 Additional Docs Submitted",
  },
  [IndividualApplicationRequiresAdditionalDocumentationKYC1]: {
    color: "yellow",
    text: "KYC1 Additional Docs Required",
  },
  [IndividualApplicationRequiresAdditionalDocumentationForAppealKYC1]: {
    color: "yellow",
    text: "KYC1 Appeal Docs Required",
  },
  [IndividualApplicationAppealSubmittedKYC1]: {
    color: "yellow",
    text: "KYC1 Appeal Submitted",
  },
  [IndividualApplicationAppealDocumentationAdminReviewPendingKYC1]: {
    color: "yellow",
    text: "KYC1 Appeal Under Review",
  },
  [IndividualApplicationAttestationSubmitted]: {
    color: "yellow",
    text: "Attestation Submitted",
  },

  [AccountApplicationApexAutoAppeal]: {
    color: "yellow",
    text: "Apex Auto Appeal",
  },
  [AccountApplicationAdditionalApexDocumentationSubmitted]: {
    color: "yellow",
    text: "Additional Apex Docs Submitted",
  },
  [AccountApplicationApexDocumentsRequired]: {
    color: "yellow",
    text: "Apex Docs Required",
  },

  // Blue - Submitted/Created states
  [EmployerAdminLoginCreated]: { color: "blue", text: "Admin Login Created" },

  [IndividualLoginCreated]: { color: "blue", text: "Login Created" },
  [IndividualInvestmentInfo]: { color: "blue", text: "Investment Info" },
  [IndividualDisclosures]: { color: "blue", text: "Disclosures" },
  [IndividualApplicationSubmittedKYC1]: {
    color: "blue",
    text: "KYC1 Submitted",
  },
  [IndividualApplicationSubmittedKYC2]: {
    color: "blue",
    text: "KYC2 Submitted",
  },
  [IndividualReopened]: { color: "blue", text: "Reopened" },

  [AccountApplicationSubmitted]: {
    color: "blue",
    text: "Application Submitted",
  },
  [BusinessMemberKYCRequested]: { color: "blue", text: "KYC Requested" },

  // Red - Denied/Failed/Locked states
  [EmployerApplicationDenied]: { color: "red", text: "Application Denied" },

  [IndividualKYC1Red]: { color: "red", text: "KYC1 Failed" },
  [IndividualKYC2Red]: { color: "red", text: "KYC2 Failed" },
  [IndividualApplicationDenied]: { color: "red", text: "Application Denied" },
  [IndividualLocked]: { color: "red", text: "Locked" },

  [AccountApplicationDenied]: { color: "red", text: "Application Denied" },
  [AccountLocked]: { color: "red", text: "Locked" },
  [BusinessMemberKYCFailed]: { color: "red", text: "KYC Failed" },

  // Gray - Closed/Dormant states
  [EmployerClosed]: { color: "gray", text: "Closed" },

  [IndividualClosed]: { color: "gray", text: "Closed" },
  [IndividualDormant]: { color: "gray", text: "Dormant" },

  [AccountClosed]: { color: "gray", text: "Closed" },
};

interface KycStatusBadgeProps {
  status: string;
  className?: string;
  size?: string;
}

const KycStatusBadge = ({
  status,
  className = "",
  size = "default",
}: KycStatusBadgeProps) => {
  const config = STATUS_CONFIG[status];

  if (!config) {
    // Default styling for unknown status
    return (
      <span
        className={classnames(
          "status-badge",
          `status-badge--${size}`,
          "status-badge--gray",
          className
        )}
      >
        {status || "Unknown"}
      </span>
    );
  }

  const classes = classnames(
    "status-badge",
    `status-badge--${size}`,
    `status-badge--${config.color}`,
    className
  );

  return <span className={classes}>{config.text}</span>;
};

export default KycStatusBadge;
